import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  trackWhatsAppClick,
  trackEventCustom,
} from "../../utils/analytics";

const serviceNavigation = [
  { title: "Custom Software Development", to: "/services/custom-software-development" },
  { title: "ERP & Business Management", to: "/services/erp-business-management" },
  { title: "CRM & Lead Management", to: "/services/crm-lead-management" },
  { title: "Healthcare Software", to: "/services/healthcare-software" },
  { title: "Inventory Management", to: "/services/inventory-management" },
  { title: "Industrial Process Software", to: "/services/industrial-process-software" },
  { title: "License Management", to: "/services/license-management" },
  { title: "Mobile App Development", to: "/services/mobile-app-development" },
];

export default function LicenseManagement() {
  useEffect(() => {
    document.title =
      "License Management Software Development Company in Pune | HexxaTech";

    const description =
      "HexxaTech develops custom license management software for managing clients, products, software licenses, activations, validations, renewals and license information from a centralized platform.";

    const setMeta = (name, content) => {
      let meta = document.querySelector(`meta[name="${name}"]`);

      if (!meta) {
        meta = document.createElement("meta");
        meta.setAttribute("name", name);
        document.head.appendChild(meta);
      }

      meta.setAttribute("content", content);
    };

    setMeta("description", description);

    let canonical = document.querySelector('link[rel="canonical"]');

    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }

    canonical.setAttribute(
      "href",
      "https://hexxatech.co.in/services/license-management"
    );

    const setPropertyMeta = (property, content) => {
      let meta = document.querySelector(`meta[property="${property}"]`);

      if (!meta) {
        meta = document.createElement("meta");
        meta.setAttribute("property", property);
        document.head.appendChild(meta);
      }

      meta.setAttribute("content", content);
    };

    setPropertyMeta(
      "og:title",
      "License Management Software Development | HexxaTech"
    );

    setPropertyMeta("og:description", description);
    setPropertyMeta("og:type", "website");
    setPropertyMeta(
      "og:url",
      "https://hexxatech.co.in/services/license-management"
    );
    setPropertyMeta("og:site_name", "HexxaTech Softwares");

    const existingSchema = document.getElementById(
      "hexxatech-license-management-schema"
    );

    if (!existingSchema) {
      const script = document.createElement("script");

      script.id = "hexxatech-license-management-schema";
      script.type = "application/ld+json";

      script.text = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Service",
        name: "License Management Software Development",
        serviceType: "License Management Software Development",
        description: description,
        provider: {
          "@type": "Organization",
          name: "HexxaTech Softwares",
          url: "https://hexxatech.co.in/",
        },
        areaServed: {
          "@type": "Country",
          name: "India",
        },
      });

      document.head.appendChild(script);
    }
  }, []);

  const features = [
    {
      icon: "▣",
      title: "Client Management",
      text: "Maintain centralized records of customers, organizations and users associated with your software products and licenses.",
    },
    {
      icon: "◈",
      title: "Product Management",
      text: "Manage software products, versions, modules, packages and product-specific licensing rules from one platform.",
    },
    {
      icon: "✓",
      title: "License Generation",
      text: "Generate and assign unique software licenses based on customers, products, plans, devices or business requirements.",
    },
    {
      icon: "◎",
      title: "License Validation",
      text: "Validate license information and determine whether a software license is active, expired, suspended or invalid.",
    },
    {
      icon: "↻",
      title: "Renewal Management",
      text: "Track license expiry dates, renewals and upcoming actions to help businesses maintain uninterrupted software access.",
    },
    {
      icon: "▤",
      title: "License Information",
      text: "View complete license details including customer, product, activation, expiry, status and associated information.",
    },
    {
      icon: "⚙",
      title: "Activation Management",
      text: "Control software activation and registration using configurable business rules and application-level validation.",
    },
    {
      icon: "▥",
      title: "Reports & Dashboard",
      text: "Monitor active licenses, expired licenses, renewals, customers and product-level licensing information through dashboards and reports.",
    },
  ];

  const modules = [
    "Customer / Client Management",
    "Software Product Management",
    "Product Version Management",
    "License Creation",
    "License Assignment",
    "License Activation",
    "License Validation",
    "License Expiry Tracking",
    "License Renewal Management",
    "License Status Management",
    "User & Access Management",
    "Reports & Dashboard",
  ];

  const benefits = [
    {
      number: "01",
      title: "Centralized License Control",
      text: "Keep customers, products, licenses, activation information and expiry details organized within one centralized application.",
    },
    {
      number: "02",
      title: "Better Software Governance",
      text: "Apply consistent licensing rules across customers, products, versions and software deployments.",
    },
    {
      number: "03",
      title: "Reduced Manual Work",
      text: "Automate license creation, validation, expiry tracking and renewal-related activities.",
    },
    {
      number: "04",
      title: "Real-Time Visibility",
      text: "Quickly understand active, expired, suspended and upcoming licenses through centralized dashboards and reports.",
    },
  ];

  const process = [
    {
      number: "01",
      title: "Understand Licensing Requirements",
      text: "We understand your software products, customers, licensing rules, activation process and business requirements.",
    },
    {
      number: "02",
      title: "Design License Architecture",
      text: "We define the database structure, license lifecycle, validation rules, user roles and application architecture.",
    },
    {
      number: "03",
      title: "Develop Management Platform",
      text: "We develop the license management application with modules for products, customers, licenses, validation and reporting.",
    },
    {
      number: "04",
      title: "Integrate & Deploy",
      text: "The system can be integrated with your existing software applications and deployed according to your infrastructure requirements.",
    },
  ];

  const currentIndex = serviceNavigation.findIndex(
    (service) => service.to === "/services/license-management"
  );
  const previousService =
    serviceNavigation[(currentIndex - 1 + serviceNavigation.length) % serviceNavigation.length];
  const nextService =
    serviceNavigation[(currentIndex + 1) % serviceNavigation.length];

  return (
    <div className="hexxa-license-page">
      <style>{`
        * {
          box-sizing: border-box;
        }

        html {
          scroll-behavior: smooth;
        }

        .hexxa-license-page {
          background: #ffffff;
          color: #0f172a;
          overflow: hidden;
          font-family: Inter, -apple-system, BlinkMacSystemFont,
            "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
        }

        .license-container {
          width: min(1180px, calc(100% - 40px));
          margin: 0 auto;
        }

        /* =========================
           HERO
        ========================= */

        .license-hero {
          position: relative;
          padding: 110px 0 90px;
          color: #ffffff;
          background:
            radial-gradient(
              circle at 82% 20%,
              rgba(59, 130, 246, 0.22),
              transparent 30%
            ),
            linear-gradient(
              135deg,
              #294a7d 0%,
              #355b91 55%,
              #294a7d 100%
            );
        }

        .license-hero::before {
          content: "";
          position: absolute;
          width: 450px;
          height: 450px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.035);
          top: -240px;
          right: -100px;
        }

        .license-hero-grid {
          position: relative;
          z-index: 1;
          display: grid;
          grid-template-columns: 1.05fr 0.95fr;
          gap: 70px;
          align-items: center;
        }

        .license-eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 7px 12px;
          border-radius: 999px;
          background: rgba(255, 255, 255, 0.1);
          border: 1px solid rgba(255, 255, 255, 0.16);
          color: #dbeafe;
          font-size: 11px;
          font-weight: 800;
          letter-spacing: 1.2px;
          text-transform: uppercase;
          margin-bottom: 20px;
        }

        .license-eyebrow-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: #60a5fa;
        }

        .license-hero h1 {
          margin: 0;
          max-width: 720px;
          font-size: clamp(40px, 5vw, 65px);
          line-height: 1.04;
          letter-spacing: -2.8px;
          font-weight: 800;
        }

        .license-hero h1 span {
          color: #60a5fa;
        }

        .license-hero-text {
          max-width: 660px;
          margin: 25px 0 30px;
          color: #dbeafe;
          font-size: 17px;
          line-height: 1.8;
        }

        .license-buttons {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
        }

        .license-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-height: 48px;
          padding: 0 20px;
          border-radius: 10px;
          text-decoration: none;
          font-size: 14px;
          font-weight: 700;
          transition: 0.25s ease;
        }

        .license-btn-primary {
          background: #2563eb;
          color: #ffffff;
          box-shadow: 0 12px 25px rgba(15, 23, 42, 0.2);
        }

        .license-btn-primary:hover {
          transform: translateY(-2px);
          background: #1d4ed8;
        }

        .license-btn-light {
          background: rgba(255, 255, 255, 0.08);
          color: #ffffff;
          border: 1px solid rgba(255, 255, 255, 0.22);
        }

        .license-btn-light:hover {
          background: rgba(255, 255, 255, 0.15);
        }

        /* =========================
           HERO VISUAL
        ========================= */

        .license-visual {
          position: relative;
          min-height: 370px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .license-panel {
          width: min(470px, 100%);
          padding: 18px;
          border-radius: 22px;
          background: rgba(255, 255, 255, 0.1);
          border: 1px solid rgba(255, 255, 255, 0.18);
          backdrop-filter: blur(10px);
          box-shadow: 0 30px 70px rgba(15, 23, 42, 0.22);
          transform: rotate(-2deg);
        }

        .license-panel-top {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding-bottom: 15px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }

        .license-panel-title {
          font-size: 12px;
          font-weight: 700;
          color: #e2e8f0;
        }

        .license-panel-status {
          padding: 5px 9px;
          border-radius: 999px;
          background: rgba(96, 165, 250, 0.18);
          color: #bfdbfe;
          font-size: 9px;
          font-weight: 800;
        }

        .license-panel-body {
          display: grid;
          gap: 12px;
          margin-top: 15px;
        }

        .license-stat-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 9px;
        }

        .license-stat-card {
          padding: 14px 10px;
          border-radius: 12px;
          background: rgba(255, 255, 255, 0.1);
          border: 1px solid rgba(255, 255, 255, 0.08);
        }

        .license-stat-card strong {
          display: block;
          font-size: 20px;
          margin-bottom: 4px;
        }

        .license-stat-card small {
          color: #cbd5e1;
          font-size: 9px;
        }

        .license-table {
          border-radius: 13px;
          background: rgba(255, 255, 255, 0.08);
          overflow: hidden;
        }

        .license-table-row {
          display: grid;
          grid-template-columns: 1.2fr 1fr 0.8fr;
          gap: 10px;
          padding: 12px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.08);
          font-size: 9px;
          color: #dbeafe;
        }

        .license-table-row:last-child {
          border-bottom: 0;
        }

        .license-table-row strong {
          color: #ffffff;
        }

        .license-active {
          color: #86efac !important;
          font-weight: 800;
        }

        /* =========================
           INTRO
        ========================= */

        .license-section {
          padding: 90px 0;
        }

        .license-soft {
          background: #f8fafc;
        }

        .license-label {
          color: #2563eb;
          font-size: 11px;
          font-weight: 800;
          letter-spacing: 1.5px;
          text-transform: uppercase;
        }

        .license-title {
          margin: 12px 0 0;
          max-width: 780px;
          font-size: clamp(32px, 4vw, 48px);
          line-height: 1.1;
          letter-spacing: -1.8px;
          color: #0f172a;
        }

        .license-description {
          margin: 18px 0 0;
          max-width: 750px;
          color: #64748b;
          font-size: 16px;
          line-height: 1.8;
        }

        .license-intro-grid {
          display: grid;
          grid-template-columns: 1.1fr 0.9fr;
          gap: 70px;
          align-items: center;
        }

        .license-intro-text p {
          color: #64748b;
          font-size: 15px;
          line-height: 1.85;
          margin: 22px 0 0;
        }

        .license-highlight {
          padding: 32px;
          border-radius: 22px;
          background: linear-gradient(145deg, #eff6ff, #ffffff);
          border: 1px solid #dbeafe;
        }

        .license-highlight h3 {
          margin: 0 0 20px;
          font-size: 20px;
        }

        .license-highlight-list {
          display: grid;
          gap: 11px;
        }

        .license-highlight-item {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px;
          border-radius: 10px;
          background: #ffffff;
          border: 1px solid #e5e7eb;
          color: #475569;
          font-size: 13px;
        }

        .license-highlight-item span {
          width: 22px;
          height: 22px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          background: #eff6ff;
          color: #2563eb;
          font-size: 11px;
          font-weight: 800;
        }

        /* =========================
           FEATURES
        ========================= */

        .license-feature-header {
          text-align: center;
          max-width: 800px;
          margin: 0 auto 45px;
        }

        .license-feature-header .license-title,
        .license-feature-header .license-description {
          margin-left: auto;
          margin-right: auto;
        }

        .license-feature-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 16px;
        }

        .license-feature-card {
          min-height: 230px;
          padding: 25px;
          border-radius: 16px;
          background: #ffffff;
          border: 1px solid #e5e7eb;
          transition: 0.3s ease;
        }

        .license-feature-card:hover {
          transform: translateY(-5px);
          border-color: #bfdbfe;
          box-shadow: 0 18px 40px rgba(15, 23, 42, 0.07);
        }

        .license-feature-icon {
          width: 42px;
          height: 42px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 11px;
          background: #eff6ff;
          color: #2563eb;
          font-size: 17px;
          font-weight: 800;
          margin-bottom: 20px;
        }

        .license-feature-card h3 {
          margin: 0 0 9px;
          font-size: 16px;
        }

        .license-feature-card p {
          margin: 0;
          color: #64748b;
          font-size: 12px;
          line-height: 1.7;
        }

        /* =========================
           MODULES
        ========================= */

        .license-module-grid {
          display: grid;
          grid-template-columns: 0.85fr 1.15fr;
          gap: 70px;
          align-items: center;
        }

        .license-module-box {
          padding: 32px;
          border-radius: 22px;
          background: #ffffff;
          border: 1px solid #e5e7eb;
          box-shadow: 0 20px 50px rgba(15, 23, 42, 0.05);
        }

        .license-module-box h3 {
          margin: 0 0 22px;
          font-size: 21px;
        }

        .license-module-list {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 10px;
        }

        .license-module-item {
          padding: 12px;
          border-radius: 10px;
          background: #f8fafc;
          color: #475569;
          font-size: 12px;
          border: 1px solid #f1f5f9;
        }

        .license-module-item::before {
          content: "✓";
          color: #2563eb;
          font-weight: 800;
          margin-right: 7px;
        }

        /* =========================
           BENEFITS
        ========================= */

        .license-benefit-list {
          display: grid;
          gap: 10px;
          margin-top: 35px;
        }

        .license-benefit {
          display: grid;
          grid-template-columns: 55px 1fr;
          gap: 15px;
          padding: 20px;
          border-radius: 14px;
          background: #ffffff;
          border: 1px solid #e5e7eb;
          transition: 0.25s ease;
        }

        .license-benefit:hover {
          border-color: #bfdbfe;
          transform: translateX(4px);
        }

        .license-benefit-number {
          color: #2563eb;
          font-size: 11px;
          font-weight: 800;
          letter-spacing: 1px;
        }

        .license-benefit h3 {
          margin: 0 0 7px;
          font-size: 15px;
        }

        .license-benefit p {
          margin: 0;
          color: #64748b;
          font-size: 12px;
          line-height: 1.7;
        }

        /* =========================
           PROCESS
        ========================= */

        .license-process-grid {
          display: grid;
          grid-template-columns: 0.9fr 1.1fr;
          gap: 70px;
          align-items: start;
        }

        .license-process-list {
          margin-top: 35px;
          display: grid;
          gap: 10px;
        }

        .license-process-item {
          padding: 20px;
          border: 1px solid #e5e7eb;
          background: #ffffff;
          border-radius: 14px;
          transition: 0.25s ease;
        }

        .license-process-item:hover {
          border-color: #bfdbfe;
          transform: translateX(4px);
        }

        .license-process-number {
          color: #2563eb;
          font-size: 11px;
          font-weight: 800;
          letter-spacing: 1px;
        }

        .license-process-item h3 {
          margin: 7px 0;
          font-size: 15px;
        }

        .license-process-item p {
          margin: 0;
          color: #64748b;
          font-size: 12px;
          line-height: 1.7;
        }

        .license-flow-box {
          padding: 35px;
          border-radius: 24px;
          background: linear-gradient(145deg, #eff6ff, #ffffff);
          border: 1px solid #dbeafe;
        }

        .license-flow {
          display: grid;
          gap: 10px;
          margin-top: 25px;
        }

        .license-flow-row {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 14px;
          background: #ffffff;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
        }

        .license-flow-icon {
          width: 30px;
          height: 30px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #eff6ff;
          color: #2563eb;
          font-size: 11px;
          font-weight: 800;
        }

        .license-flow-row strong {
          font-size: 12px;
        }

        /* =========================
           CTA
        ========================= */

        .license-cta {
          padding: 85px 0;
        }

        .license-cta-box {
          position: relative;
          overflow: hidden;
          padding: 70px 30px;
          text-align: center;
          border-radius: 24px;
          color: #ffffff;
          background:
            radial-gradient(
              circle at 85% 15%,
              rgba(59, 130, 246, 0.3),
              transparent 30%
            ),
            linear-gradient(135deg, #0f172a, #172554);
        }

        .license-cta-box::before {
          content: "";
          position: absolute;
          width: 300px;
          height: 300px;
          border-radius: 50%;
          border: 1px solid rgba(255, 255, 255, 0.08);
          top: -170px;
          left: -80px;
        }

        .license-cta-box > * {
          position: relative;
          z-index: 1;
        }

        .license-cta-label {
          display: inline-block;
          padding: 7px 12px;
          border-radius: 999px;
          background: rgba(255, 255, 255, 0.08);
          border: 1px solid rgba(255, 255, 255, 0.1);
          color: #bfdbfe;
          font-size: 10px;
          font-weight: 800;
          letter-spacing: 1px;
          text-transform: uppercase;
        }

        .license-cta h2 {
          max-width: 720px;
          margin: 20px auto 12px;
          font-size: clamp(34px, 4vw, 50px);
          line-height: 1.1;
          letter-spacing: -1.5px;
        }

        .license-cta p {
          max-width: 680px;
          margin: 0 auto 28px;
          color: #cbd5e1;
          font-size: 15px;
          line-height: 1.8;
        }

        .license-cta .license-btn-primary {
          background: #ffffff;
          color: #0f172a;
        }

        .license-cta .license-btn-primary:hover {
          background: #eff6ff;
        }

        /* =========================
           SEO CONTENT
        ========================= */

        .license-seo {
          padding: 0 0 70px;
        }

        .license-seo-inner {
          max-width: 900px;
        }

        .license-seo h2 {
          margin: 0 0 15px;
          font-size: 24px;
        }

        .license-seo h3 {
          margin: 28px 0 10px;
          font-size: 19px;
        }

        .license-seo p {
          margin: 0;
          color: #64748b;
          font-size: 14px;
          line-height: 1.8;
        }

        /* =========================
           RESPONSIVE
        ========================= */

        @media (max-width: 1000px) {
          .license-hero-grid {
            grid-template-columns: 1fr;
            gap: 45px;
          }

          .license-feature-grid {
            grid-template-columns: repeat(2, 1fr);
          }

          .license-intro-grid,
          .license-module-grid,
          .license-process-grid {
            grid-template-columns: 1fr;
            gap: 45px;
          }
        }

        @media (max-width: 700px) {
          .license-container {
            width: min(100% - 28px, 1180px);
          }

          .license-hero {
            padding: 75px 0 65px;
          }

          .license-hero h1 {
            font-size: 42px;
            letter-spacing: -1.8px;
          }

          .license-hero-text {
            font-size: 15px;
          }

          .license-panel {
            transform: none;
          }

          .license-feature-grid {
            grid-template-columns: 1fr;
          }

          .license-section {
            padding: 65px 0;
          }

          .license-module-list {
            grid-template-columns: 1fr;
          }

          .license-flow-box {
            padding: 22px;
          }

          .license-cta {
            padding: 65px 0;
          }

          .license-cta-box {
            padding: 50px 20px;
            border-radius: 18px;
          }
        }
      `}</style>

      {/* =========================================
          HERO
      ========================================= */}

      <section className="license-hero">
        <div className="license-container license-hero-grid">
          <div>
            <div className="license-eyebrow">
              <span className="license-eyebrow-dot"></span>
              License Management Software
            </div>

            <h1>
              Control your software
              <br />
              <span>licenses from one platform.</span>
            </h1>

            <p className="license-hero-text">
              HexxaTech develops custom license management software that
              helps businesses manage customers, products, software licenses,
              activations, validations, renewals and license information from
              a centralized system.
            </p>

            <div className="license-buttons">
              <a
                href="https://wa.me/919226591664?text=Hello%20HexxaTech%2C%20I%20would%20like%20to%20discuss%20a%20license%20management%20software%20project."
                target="_blank"
                rel="noopener noreferrer"
                className="license-btn license-btn-primary"
                onClick={() =>
                  trackWhatsAppClick("license_management_hero")
                }
              >
                Discuss Your Project →
              </a>

              <Link
                to="/case-studies/wisetech"
                className="license-btn license-btn-light"
                onClick={() =>
                  trackEventCustom("license_case_study_click", {
                    location: "license_management_hero",
                  })
                }
              >
                View Related Case Study →
              </Link>
            </div>
          </div>

          <div className="license-visual">
            <div className="license-panel">
              <div className="license-panel-top">
                <span className="license-panel-title">
                  License Management
                </span>

                <span className="license-panel-status">
                  SYSTEM ACTIVE
                </span>
              </div>

              <div className="license-panel-body">
                <div className="license-stat-grid">
                  <div className="license-stat-card">
                    <strong>248</strong>
                    <small>Active Licenses</small>
                  </div>

                  <div className="license-stat-card">
                    <strong>32</strong>
                    <small>Renewals</small>
                  </div>

                  <div className="license-stat-card">
                    <strong>18</strong>
                    <small>Products</small>
                  </div>
                </div>

                <div className="license-table">
                  <div className="license-table-row">
                    <strong>Customer</strong>
                    <strong>Product</strong>
                    <strong>Status</strong>
                  </div>

                  <div className="license-table-row">
                    <span>ABC Industries</span>
                    <span>Enterprise</span>
                    <span className="license-active">Active</span>
                  </div>

                  <div className="license-table-row">
                    <span>XYZ Solutions</span>
                    <span>Professional</span>
                    <span className="license-active">Active</span>
                  </div>

                  <div className="license-table-row">
                    <span>Tech Systems</span>
                    <span>Standard</span>
                    <span className="license-active">Active</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* =========================================
          INTRODUCTION
      ========================================= */}

      <section className="license-section">
        <div className="license-container license-intro-grid">
          <div className="license-intro-text">
            <span className="license-label">
              Custom License Management
            </span>

            <h2 className="license-title">
              Manage software licensing without manual complexity.
            </h2>

            <p>
              Software companies often need to manage multiple customers,
              products, versions, licenses and activation records. Managing
              this information manually can make it difficult to track license
              status, expiry dates and customer-specific software access.
            </p>

            <p>
              HexxaTech builds custom license management systems that
              centralize these activities into a structured platform. The
              system can be designed around your licensing model, business
              rules and existing software architecture.
            </p>

            <p>
              Whether you need a simple internal license management system or
              a complete platform integrated with your software product, we
              can build the solution around your requirements.
            </p>
          </div>

          <div className="license-highlight">
            <h3>
              What you can manage
            </h3>

            <div className="license-highlight-list">
              {[
                "Customers & Organizations",
                "Software Products",
                "Product Versions",
                "License Keys",
                "Activations",
                "License Status",
                "Expiry Dates",
                "Renewals",
              ].map((item) => (
                <div className="license-highlight-item" key={item}>
                  <span>✓</span>
                  {item}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* =========================================
          FEATURES
      ========================================= */}

      <section className="license-section license-soft">
        <div className="license-container">
          <div className="license-feature-header">
            <span className="license-label">
              License Management Features
            </span>

            <h2 className="license-title">
              Everything you need to manage software licenses.
            </h2>

            <p className="license-description">
              Build a centralized license management platform that gives your
              team complete visibility into customers, products, licenses,
              activations and renewals.
            </p>
          </div>

          <div className="license-feature-grid">
            {features.map((feature) => (
              <article
                className="license-feature-card"
                key={feature.title}
                onClick={() =>
                  trackEventCustom("license_feature_interest", {
                    feature: feature.title,
                  })
                }
              >
                <div className="license-feature-icon">
                  {feature.icon}
                </div>

                <h3>{feature.title}</h3>

                <p>{feature.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* =========================================
          MODULES
      ========================================= */}

      <section className="license-section">
        <div className="license-container license-module-grid">
          <div>
            <span className="license-label">
              Software Modules
            </span>

            <h2 className="license-title">
              A licensing platform designed around your business model.
            </h2>

            <p className="license-description">
              Every software business can have different licensing rules.
              Your license management application can be customized with the
              modules and workflows required by your organization.
            </p>

            <div style={{ marginTop: "28px" }}>
              <a
                href="https://wa.me/919226591664?text=Hello%20HexxaTech%2C%20I%20want%20to%20discuss%20custom%20license%20management%20software."
                target="_blank"
                rel="noopener noreferrer"
                className="license-btn license-btn-primary"
                onClick={() =>
                  trackWhatsAppClick("license_modules_cta")
                }
              >
                Discuss Requirements →
              </a>
            </div>
          </div>

          <div className="license-module-box">
            <h3>
              Common License Management Modules
            </h3>

            <div className="license-module-list">
              {modules.map((module) => (
                <div className="license-module-item" key={module}>
                  {module}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* =========================================
          BENEFITS
      ========================================= */}

      <section className="license-section license-soft">
        <div className="license-container">
          <div style={{ maxWidth: "780px" }}>
            <span className="license-label">
              Business Benefits
            </span>

            <h2 className="license-title">
              More control over your software licensing operations.
            </h2>

            <p className="license-description">
              A centralized license management system can reduce manual
              tracking, improve visibility and help your team manage the
              complete lifecycle of software licenses.
            </p>
          </div>

          <div className="license-benefit-list">
            {benefits.map((benefit) => (
              <div
                className="license-benefit"
                key={benefit.number}
              >
                <div className="license-benefit-number">
                  {benefit.number}
                </div>

                <div>
                  <h3>{benefit.title}</h3>

                  <p>{benefit.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* =========================================
          DEVELOPMENT PROCESS
      ========================================= */}

      <section className="license-section">
        <div className="license-container license-process-grid">
          <div>
            <span className="license-label">
              Development Process
            </span>

            <h2 className="license-title">
              From licensing requirements to a working platform.
            </h2>

            <p className="license-description">
              We first understand how your software is licensed and then
              design the management system around your business rules,
              products and customers.
            </p>

            <div className="license-process-list">
              {process.map((step) => (
                <div
                  className="license-process-item"
                  key={step.number}
                >
                  <div className="license-process-number">
                    {step.number}
                  </div>

                  <h3>{step.title}</h3>

                  <p>{step.text}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="license-flow-box">
            <span className="license-label">
              License Lifecycle
            </span>

            <h3
              style={{
                margin: "12px 0 0",
                fontSize: "23px",
              }}
            >
              From customer registration to license renewal.
            </h3>

            <div className="license-flow">
              {[
                "Customer Registration",
                "Software Product Selection",
                "License Creation",
                "License Activation",
                "License Validation",
                "Usage & Status Monitoring",
                "Expiry Tracking",
                "License Renewal",
              ].map((item, index) => (
                <div className="license-flow-row" key={item}>
                  <div className="license-flow-icon">
                    {String(index + 1).padStart(2, "0")}
                  </div>

                  <strong>{item}</strong>
                </div>
              ))}
            </div>

            <p
              style={{
                margin: "22px 0 0",
                color: "#64748b",
                fontSize: "13px",
                lineHeight: 1.7,
              }}
            >
              The complete license lifecycle can be customized according to
              your product, customer and licensing requirements.
            </p>
          </div>
        </div>
      </section>

      {/* =========================================
          RELATED CASE STUDY
      ========================================= */}

      <section className="license-section license-soft">
        <div
          className="license-container"
          style={{
            maxWidth: "900px",
            textAlign: "center",
          }}
        >
          <span className="license-label">
            Related Project
          </span>

          <h2 className="license-title" style={{ marginLeft: "auto", marginRight: "auto" }}>
            WiseTech License Management Platform
          </h2>

          <p
            className="license-description"
            style={{ marginLeft: "auto", marginRight: "auto" }}
          >
            Explore how HexxaTech developed a centralized license management
            platform for managing clients, software products, license
            allocation, validation and real-time license information.
          </p>

          <div style={{ marginTop: "28px" }}>
            <Link
              to="/case-studies/wisetech"
              className="license-btn license-btn-primary"
              onClick={() =>
                trackEventCustom("related_case_study_click", {
                  case_study: "WiseTech",
                })
              }
            >
              View WiseTech Case Study →
            </Link>
          </div>
        </div>
      </section>

      {/* =========================================
          SERVICE NAVIGATION
      ========================================= */}

      <section
        style={{
          padding: "0 0 70px",
          background: "#ffffff",
        }}
      >
        <div
          style={{
            width: "min(100% - 32px, 1100px)",
            margin: "0 auto",
            maxWidth: "1100px",
          }}
        >
          <span className="license-label">
            Explore Our Services
          </span>

          <h2 className="license-title" style={{ marginTop: "12px" }}>
            Explore our other software development services.
          </h2>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: "12px",
              marginTop: "22px",
            }}
          >
            {serviceNavigation.map((service) => {
              const isCurrent = service.to === "/services/license-management";

              return (
                <Link
                  key={service.to}
                  to={service.to}
                  style={{
                    display: "block",
                    padding: "16px 18px",
                    borderRadius: "12px",
                    textDecoration: "none",
                    border: isCurrent ? "1px solid #2563eb" : "1px solid #e5e7eb",
                    background: isCurrent ? "#eff6ff" : "#f8fafc",
                    color: isCurrent ? "#1d4ed8" : "#334155",
                    fontWeight: isCurrent ? 700 : 600,
                  }}
                >
                  {service.title}
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <section
        style={{
          padding: "0 0 70px",
          background: "#ffffff",
        }}
      >
        <div
          style={{
            width: "min(100% - 32px, 1100px)",
            margin: "0 auto",
            maxWidth: "1100px",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: "12px",
              flexWrap: "wrap",
              borderTop: "1px solid #e5e7eb",
              paddingTop: "20px",
            }}
          >
            <Link
              to={previousService.to}
              style={{ color: "#2563eb", textDecoration: "none", fontWeight: 600 }}
            >
              ← Previous: {previousService.title}
            </Link>

            <Link
              to={nextService.to}
              style={{ color: "#2563eb", textDecoration: "none", fontWeight: 600 }}
            >
              Next: {nextService.title} →
            </Link>
          </div>
        </div>
      </section>

      {/* =========================================
          CTA
      ========================================= */}

      <section className="license-cta">
        <div className="license-container">
          <div className="license-cta-box">
            <span className="license-cta-label">
              Build Your License Management System
            </span>

            <h2>
              Need better control over your software licenses?
            </h2>

            <p>
              Tell us about your software products, customers, licensing
              rules and business requirements. HexxaTech can help you design
              and develop a custom license management platform around your
              workflow.
            </p>

            <a
              href="https://wa.me/919226591664?text=Hello%20HexxaTech%2C%20I%20would%20like%20to%20discuss%20custom%20license%20management%20software."
              target="_blank"
              rel="noopener noreferrer"
              className="license-btn license-btn-primary"
              onClick={() =>
                trackWhatsAppClick("license_management_bottom_cta")
              }
            >
              Discuss Your Project →
            </a>
          </div>
        </div>
      </section>

      {/* =========================================
          SEO CONTENT
      ========================================= */}

      <section className="license-seo">
        <div className="license-container license-seo-inner">
          <h2>
            License Management Software Development Company in Pune
          </h2>

          <p>
            HexxaTech Softwares develops custom license management software
            for businesses that need to manage software products, customers,
            licenses, activations, validation, expiry dates and renewals from
            a centralized platform. Our license management solutions can be
            designed around your existing software products and licensing
            processes.
          </p>

          <h3>
            Custom License Management Software
          </h3>

          <p>
            Instead of using generic license tracking tools, businesses can
            implement a custom license management system that follows their
            own licensing model. The platform can include customer management,
            product management, license generation, activation, validation,
            expiry tracking, renewal management, dashboards and reports.
          </p>

          <h3>
            License Management Solutions for Software Companies
          </h3>

          <p>
            Our solutions can help software companies centralize licensing
            information and improve visibility across customers and products.
            Depending on requirements, the system can also be integrated with
            existing desktop, web or mobile applications to support license
            validation and application access workflows.
          </p>

          <h3>
            License Management Software in Pune and Across India
          </h3>

          <p>
            HexxaTech Softwares provides custom software development services
            in Pune and across India. We build business applications around
            real operational requirements, with a focus on practical
            workflows, scalable architecture, centralized data and long-term
            maintainability.
          </p>
        </div>
      </section>
    </div>
  );
}